/**
 * 🐑⚠️ **YOUR SHEEP ARE STARVING — the warning that cannot be missed (T286).**
 *
 * 🎯 **haugur, 2026-10-01:** *"we need to find a clever way to warn a player of
 * imminent death of his animals, a short note in hud will not cut it."* He chose
 * two of four:
 *
 * | | |
 * |---|---|
 * | 🧭 **An arrow at the screen edge** | pointing at the starving flock wherever you are in Iceland, **amber turning red as death nears**, pulsing in the last two days. When the flock is in view it sits over it instead |
 * | 🚪 **A warning at login** | the first thing you see when you join, with **a button that opens the World map on the flock** |
 *
 * 🔴 **Everything here is told by the server** (`flock_warning`, protocol 48):
 * only YOUR sheep, only when the list changes, and once at every login. **An
 * empty list takes the arrow away.**
 *
 * ⚠️ **DOM, not the SVG HUD**, because it moves every frame — the HUD cluster
 * is rebuilt on change, not per frame (`hud-cluster.ts`), and the nameplates
 * already live in the DOM for the same reason (`hud.ts`).
 */

import { Vector3, type Camera } from 'three';
import { STARVING_BELOW, t, type FlockWarningMessage } from '@lofi/shared';

type Flock = FlockWarningMessage['flocks'][number];

/** 🎨 Amber at the start of starving, red at the end — mixed by days left. */
function colourFor(daysLeft: number): string {
  const f = Math.min(1, Math.max(0, daysLeft / STARVING_BELOW));
  const mix = (a: number, b: number): number => Math.round(b + (a - b) * f);
  return `rgb(${String(mix(232, 224))}, ${String(mix(160, 48))}, ${String(mix(48, 32))})`;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/** The arrowhead — pointing UP, so a rotation of θ points it θ clockwise. */
function arrowSvg(): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '-20 -20 40 40');
  svg.setAttribute('width', '64');
  svg.setAttribute('height', '64');
  svg.style.display = 'block';
  svg.style.overflow = 'visible';
  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', 'M 0 -17 L 13 9 L 0 3 L -13 9 Z');
  path.setAttribute('fill', 'currentColor');
  path.setAttribute('stroke', '#1a1208');
  path.setAttribute('stroke-width', '2.5');
  path.setAttribute('stroke-linejoin', 'round');
  svg.append(path);
  return svg;
}

export class FlockWarning {
  private flocks: readonly Flock[] = [];
  private readonly arrow: HTMLDivElement;
  private readonly pointer: HTMLDivElement;
  private readonly label: HTMLDivElement;
  private panel: HTMLDivElement | null = null;
  /** 🚪 Show the login panel on the next non-empty warning — armed by every welcome. */
  private armed = false;
  private readonly projected = new Vector3();

  private readonly onShowMap: (x: number, z: number, label: string) => void;

  constructor(onShowMap: (x: number, z: number, label: string) => void) {
    this.onShowMap = onShowMap;
    const style = document.createElement('style');
    style.textContent = `
      @keyframes flockwarn-pulse { 0%,100% { transform: scale(1) } 50% { transform: scale(1.25) } }
      .flockwarn { position: fixed; z-index: 30; pointer-events: none; display: none;
        transform: translate(-50%, -50%); text-align: center; }
      .flockwarn .pointer { display: flex; justify-content: center; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.7)); }
      /* 🔴 A DARK PILL, one line, never clipped — 160's first bake cut the words
         off at the screen edge and red on a pale sky could not be read. */
      .flockwarn .label { display: inline-block; white-space: nowrap; margin-top: 4px;
        padding: 4px 10px; border-radius: 6px; background: rgba(20, 12, 8, 0.86);
        border-left: 4px solid currentColor; color: #fff2e8;
        font: 700 16px/1.25 'Alegreya Sans', 'Segoe UI', sans-serif; }
      .flockwarn .pointer.pulse svg { animation: flockwarn-pulse 0.9s ease-in-out infinite; }
      .flockwarn-panel { position: fixed; z-index: 40; left: 50%; top: 18%;
        transform: translateX(-50%); min-width: 420px; max-width: 560px;
        background: rgba(24, 14, 10, 0.94); border: 2px solid #e0402a; border-radius: 10px;
        padding: 18px 22px 16px; color: #f1e6da; box-shadow: 0 8px 30px rgba(0,0,0,0.6);
        font: 16px/1.4 'Alegreya Sans', 'Segoe UI', sans-serif; }
      .flockwarn-panel h2 { margin: 0 0 10px; font: 700 22px 'Alegreya', Georgia, serif; color: #ff8a6a; }
      .flockwarn-panel p { margin: 4px 0; }
      .flockwarn-panel .buttons { display: flex; gap: 10px; justify-content: flex-end; margin-top: 14px; }
      .flockwarn-panel button { font: 600 15px 'Alegreya Sans', 'Segoe UI', sans-serif;
        padding: 7px 16px; border-radius: 6px; border: 1px solid #8a5a40; cursor: pointer;
        background: #3a2418; color: #f1e6da; }
      .flockwarn-panel button.map { background: #b8432c; border-color: #e0604a; }
    `;
    document.head.appendChild(style);

    this.arrow = document.createElement('div');
    this.arrow.className = 'flockwarn';
    this.pointer = document.createElement('div');
    this.pointer.className = 'pointer';
    this.pointer.append(arrowSvg());
    this.label = document.createElement('div');
    this.label.className = 'label';
    this.arrow.append(this.pointer, this.label);
    document.body.appendChild(this.arrow);
  }

  /** 🚪 A fresh connection — the next warning with anything in it opens the panel. */
  armLogin(): void {
    this.armed = true;
  }

  /** What the server says is starving now. Empty takes the arrow away. */
  set(flocks: readonly Flock[]): void {
    this.flocks = flocks;
    if (flocks.length === 0) {
      this.arrow.style.display = 'none';
      return;
    }
    if (this.armed) {
      this.armed = false;
      this.showPanel(flocks);
    }
  }

  /** 🚪 The login warning, with the button to the map. */
  private showPanel(flocks: readonly Flock[]): void {
    this.panel?.remove();
    const panel = document.createElement('div');
    panel.className = 'flockwarn-panel';
    const title = document.createElement('h2');
    title.textContent = t('flock.login.title');
    panel.append(title);
    for (const f of flocks) {
      const line = document.createElement('p');
      line.textContent = t('flock.login.line', {
        count: f.count,
        days: f.daysLeft.toFixed(1),
      });
      panel.append(line);
    }
    const buttons = document.createElement('div');
    buttons.className = 'buttons';
    const first = flocks[0];
    if (first !== undefined) {
      const map = document.createElement('button');
      map.className = 'map';
      map.textContent = t('flock.login.map');
      map.addEventListener('click', () => {
        panel.remove();
        this.panel = null;
        this.onShowMap(first.x, first.z, t('flock.map.label', { count: first.count }));
      });
      buttons.append(map);
    }
    const close = document.createElement('button');
    close.textContent = t('flock.login.close');
    close.addEventListener('click', () => {
      panel.remove();
      this.panel = null;
    });
    buttons.append(close);
    panel.append(buttons);
    document.body.appendChild(panel);
    this.panel = panel;
  }

  /**
   * 🧭 **Every frame: point at the most urgent flock.**
   *
   * @param player WORLD metres — the arrow's label says how far.
   * @param renderOrigin taken off here, exactly as `hud.ts` `pinToScreen` does —
   *   skipping it is what once drew bodies 347 km outside the world.
   */
  update(camera: Camera, renderOrigin: Vector3, player: { x: number; y: number; z: number }): void {
    const worst = this.flocks[0];
    if (worst === undefined) return;
    this.projected.set(
      worst.x - renderOrigin.x,
      player.y - renderOrigin.y,
      worst.z - renderOrigin.z,
    );
    this.projected.project(camera);
    const behind = this.projected.z > 1;
    let sx = behind ? -this.projected.x : this.projected.x;
    let sy = behind ? -this.projected.y : this.projected.y;
    const onScreen = !behind && Math.abs(sx) < 0.85 && Math.abs(sy) < 0.8;

    let angle = 180;
    if (!onScreen) {
      /* To the edge, along the line from the middle of the screen. */
      if (sx === 0 && sy === 0) sy = -1;
      const scale = 1 / Math.max(Math.abs(sx) / 0.86, Math.abs(sy) / 0.74);
      sx *= scale;
      sy *= scale;
      angle = (Math.atan2(sx, sy) * 180) / Math.PI;
    }

    const metres = Math.round(Math.hypot(worst.x - player.x, worst.z - player.z));
    const colour = colourFor(worst.daysLeft);
    /* 🔴 The words FIRST — the edge clamp below measures them, and measuring
       last frame's text cut the first bake's label off the screen. */
    this.label.textContent = t('flock.arrow', {
      count: worst.count,
      days: worst.daysLeft.toFixed(1),
      metres,
    });
    this.arrow.style.display = 'block';
    this.arrow.style.color = colour;
    /* 🔴 Kept wholly ON the screen — the label is wider than the arrow, so the
       arrow's own point is not enough to keep its words in view. */
    const halfW = Math.max(40, this.arrow.offsetWidth / 2) + 10;
    const halfH = Math.max(40, this.arrow.offsetHeight / 2) + 10;
    const px = Math.min(
      window.innerWidth - halfW,
      Math.max(halfW, ((sx + 1) / 2) * window.innerWidth),
    );
    const py = Math.min(
      window.innerHeight - halfH,
      Math.max(halfH, ((-sy + 1) / 2) * window.innerHeight),
    );
    this.arrow.style.left = `${String(px)}px`;
    this.arrow.style.top = `${String(py)}px`;
    this.pointer.style.transform = `rotate(${String(angle)}deg)`;
    this.pointer.classList.toggle('pulse', worst.daysLeft < 2);
  }
}
